// src/components/SubmitTime/hooks/useSubmitTimeForm.ts
// Master hook that orchestrates all SubmitTime hooks
// Combines time calculations, validation, custom fields, time tracking, and cascade loading

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTimeCalculations } from './useTimeCalculations';
import { useValidation } from './useValidation';
import { useCustomFields } from './useCustomFields';
import { useTimeTracking } from './useTimeTracking';
import { useCascadeLoading } from './useCascadeLoading';
import type { SubmitTimeProps, Attachment } from '../types';
import { isRealPhase } from '../types';

export interface SubmitTimeFormHelpers {
  // Form state
  hours: string;
  setHours: (hours: string) => void;
  date: string;
  setDate: (date: string) => void;
  description: string;
  setDescription: (description: string) => void;
  body: string;
  setBody: (body: string) => void;
  attachments: Attachment[];
  setAttachments: (attachments: Attachment[]) => void;

  // Special fields
  estimatedField: string;
  setEstimatedField: (value: string) => void;
  remainingField: string;
  setRemainingField: (value: string) => void;
  actualField: string;
  setActualField: (value: string) => void;

  // Custom field IDs for remaining/actual
  remainingId: number | undefined;
  setRemainingId: (id: number | undefined) => void;
  actualId: number | undefined;
  setActualId: (id: number | undefined) => void;

  // Modal state
  openmodal: boolean;
  setOpenmodal: (open: boolean) => void;
  isEdit: boolean;
  btnSubmit: boolean;
  setBtnSubmit: (value: boolean) => void;

  // Delete dialog
  isOpenQuestionDelete: boolean;
  setIsOpenQuestionDelete: (open: boolean) => void;

  // Warnings
  warningReplyId: number;
  setWarningReplyId: (id: number) => void;
  warningRemainingDays: boolean;
  setWarningRemainingDays: (value: boolean) => void;
  remindRemaining: boolean;
  setRemindRemaining: (value: boolean) => void;

  // All hooks
  timeCalculations: ReturnType<typeof useTimeCalculations>;
  validation: ReturnType<typeof useValidation>;
  customFields: ReturnType<typeof useCustomFields>;
  timeTracking: ReturnType<typeof useTimeTracking>;
  cascadeLoading: ReturnType<typeof useCascadeLoading>;

  // Initial values and setters
  hoursInitial: string;
  setHoursInitial: (value: string) => void;
  remainingFieldInitial: string;
  setRemainingFieldInitial: (value: string) => void;
  actualFieldInitial: string;
  setActualFieldInitial: (value: string) => void;

  // Format flag
  hoursFormat: boolean;

  // Methods
  applyTimeFieldBaselines: (baselines: TimeFieldBaselines) => void;
  handleHoursChange: (value: string) => void;
  handleHoursBlur: () => void;
  handleDateChange: (value: string) => void;
  handleEstimatedChange: (value: string) => void;
  handleRemainingChange: (value: string) => void;
  handleDescriptionChange: (value: string) => void;
  handleBodyChange: (value: string) => void;
  handleAttachmentAdd: (attachment: Attachment) => void;
  handleAttachmentRemove: (attachmentId: number) => void;
  resetForm: (keepSelections?: boolean) => void;
  validateForm: () => boolean;
}

// Stored Remaining/Actual values (and their custom field IDs) for the selected task/phase + layer
export interface TimeFieldBaselines {
  remaining: string;
  actual: string;
  remainingId: number | undefined;
  actualId: number | undefined;
}

export function useSubmitTimeForm(props: SubmitTimeProps): SubmitTimeFormHelpers
{
  const {
    typesubmittime,
    conversation: _conversation,
    isedit,
    objedit: _objedit,
    openmodal: propsOpenModal,
  } = props;

  // Initialize timezone adjustments
  const hoursAdjust = new Date('2023-01-01T00:00:00').getHours() * -1;
  const minutesAdjust = new Date('2023-01-01T00:00:00').getMinutes() * -1;

  // Initialize all hooks
  const timeCalculations = useTimeCalculations(hoursAdjust, minutesAdjust);
  const validation = useValidation();
  const customFields = useCustomFields();
  const timeTracking = useTimeTracking();
  const cascadeLoading = useCascadeLoading();

  // Form state
  const todayStr = () =>
  {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const [hours, setHours] = useState<string>('');
  const [date, setDate] = useState<string>(todayStr);
  const [description, setDescription] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  // Special fields
  const [estimatedField, setEstimatedField] = useState<string>('');
  const [remainingField, setRemainingField] = useState<string>('');
  const [actualField, setActualField] = useState<string>('');

  // Snapshot values captured when the modal opens — used to recalculate remaining/actual on hours blur
  const [hoursInitial, setHoursInitial] = useState<string>('');
  const [remainingFieldInitial, setRemainingFieldInitial] = useState<string>('');
  const [actualFieldInitial, setActualFieldInitial] = useState<string>('');

  // Custom field IDs for remaining/actual (used when building time tracking request)
  const [remainingId, setRemainingId] = useState<number | undefined>(undefined);
  const [actualId, setActualId] = useState<number | undefined>(undefined);

  // Latest hours values for callbacks invoked from stale closures (phase/layer change chain)
  const hoursRef = useRef(hours);
  hoursRef.current = hours;
  const hoursInitialRef = useRef(hoursInitial);
  hoursInitialRef.current = hoursInitial;

  // Hours format error (shown when user enters letters or invalid format)
  const [hoursFormat, setHoursFormat] = useState<boolean>(false);

  // Modal state
  const [openmodal, setOpenmodal] = useState<boolean>(propsOpenModal);
  const [btnSubmit, setBtnSubmit] = useState<boolean>(false);

  // Delete dialog
  const [isOpenQuestionDelete, setIsOpenQuestionDelete] = useState<boolean>(false);

  // Warnings
  const [warningReplyId, setWarningReplyId] = useState<number>(0);
  const [warningRemainingDays, setWarningRemainingDays] = useState<boolean>(false);
  const [remindRemaining, setRemindRemaining] = useState<boolean>(false);

  // Sync openmodal with props
  useEffect(() =>
  {
    setOpenmodal(propsOpenModal);
  }, [propsOpenModal]);

  // Determine if edit mode
  const isEdit = isedit || false;

  // onChange: just store raw value + basic required validation
  const handleHoursChange = useCallback(
    (value: string) =>
    {
      setHours(value);
      const isEmpty = !value || value === '0:00' || value === '00:00' || value === '0';
      validation.updateValidation('hours', isEmpty);
      setHoursFormat(false);
    },
    [validation],
  );

  // Convert any time value (HH:MM or decimal) to decimal hours — without rounding
  const toDecimalHours = useCallback((val: string): number =>
  {
    if (!val) return 0;
    const s = val.toString().trim();
    if (s.includes(':'))
    {
      const [h, m] = s.split(':').map(Number);
      return (h ?? 0) + (m || 0) / 60;
    }
    return parseFloat(s) || 0;
  }, []);

  // Recalculate remaining and actual as decimal numbers from the given baselines
  const applyHoursToTimeFields = useCallback(
    (parsedHours: string, initialHours: string, baselines: TimeFieldBaselines) =>
    {
      const newDec = toDecimalHours(parsedHours);
      const initDec = toDecimalHours(initialHours);

      // Recalculate remaining when the field exists (remainingId defined), even if never filled.
      // Use '0' as the base when the baseline is empty so first entry subtracts correctly.
      if (baselines.remainingId !== undefined)
      {
        const remInit = toDecimalHours(baselines.remaining || '0');
        // Edit: restore original hours then subtract new; New: just subtract new
        const newRem = Math.max(0, isEdit ? remInit + initDec - newDec : remInit - newDec);
        setRemainingField(parseFloat(newRem.toFixed(2)).toString());
      }

      // Recalculate actual when the field exists (actualId defined), even if it was never filled.
      // Use '0' as the base when the baseline is empty so first entry accumulates correctly.
      if (baselines.actualId !== undefined)
      {
        const actInit = toDecimalHours(baselines.actual || '0');
        // Edit: remove original hours then add new; New: just add new
        const newAct = Math.max(0, isEdit ? actInit - initDec + newDec : actInit + newDec);
        setActualField(parseFloat(newAct.toFixed(2)).toString());
      }
    },
    [toDecimalHours, isEdit],
  );

  // Load Remaining/Actual baselines for a new task/phase/layer and re-apply the hours already
  // entered, so switching phase or layer keeps the logged time reflected in Remaining/Actual
  const applyTimeFieldBaselines = useCallback(
    (baselines: TimeFieldBaselines) =>
    {
      setRemainingFieldInitial(baselines.remaining);
      setActualFieldInitial(baselines.actual);
      setRemainingId(baselines.remainingId);
      setActualId(baselines.actualId);
      setRemainingField(baselines.remaining);
      setActualField(baselines.actual);

      // Same guards as handleHoursBlur: skip invalid, empty or out-of-range hours
      const parsed = timeCalculations.parseTime(hoursRef.current);
      if (parsed === '' || parsed === '0:00' || parsed === '00:00') return;
      const hourNum = parseInt(parsed.split(':')[0] ?? '0') || 0;
      const maxHours = timeTracking.timeTrackingTeam ? 250 : 24;
      if (hourNum >= maxHours) return;

      applyHoursToTimeFields(parsed, hoursInitialRef.current, baselines);
    },
    [timeCalculations, timeTracking.timeTrackingTeam, applyHoursToTimeFields],
  );

  // onBlur: parse + format + validate max hours + update remaining/actual
  const handleHoursBlur = useCallback(() =>
  {
    const parsed = timeCalculations.parseTime(hours);

    if (parsed === '')
    {
      if (hours !== '') setHoursFormat(true);
      setHours('00:00');
      validation.updateValidation('hours', true);
      return;
    }

    setHoursFormat(false);

    // Max hours validation
    const [hourPart] = parsed.split(':');
    const hourNum = parseInt(hourPart ?? '0') || 0;
    const maxHours = timeTracking.timeTrackingTeam ? 250 : 24;
    if (hourNum >= maxHours)
    {
      setHours(parsed);
      validation.updateValidation('hoursOutOfRange', true);
      validation.updateValidation('hours', true);
      return;
    }

    validation.updateValidation('hoursOutOfRange', false);
    setHours(parsed);

    const isEmpty = parsed === '0:00' || parsed === '00:00';
    validation.updateValidation('hours', isEmpty);

    applyHoursToTimeFields(parsed, hoursInitial, {
      remaining: remainingFieldInitial,
      actual: actualFieldInitial,
      remainingId,
      actualId,
    });
  }, [
    hours,
    timeCalculations,
    applyHoursToTimeFields,
    timeTracking.timeTrackingTeam,
    validation,
    hoursInitial,
    remainingFieldInitial,
    remainingId,
    actualFieldInitial,
    actualId,
  ]);

  // Handle date change
  const handleDateChange = useCallback((value: string) =>
  {
    setDate(value);
  }, []);

  // Handle estimated change
  const handleEstimatedChange = useCallback(
    (value: string) =>
    {
      const parsedEstimated = timeCalculations.parseTime(value);
      setEstimatedField(parsedEstimated);
    },
    [timeCalculations],
  );

  // Handle remaining change — numeric value (e.g. "5.5"), not HH:MM
  const handleRemainingChange = useCallback((value: string) =>
  {
    setRemainingField(value);
  }, []);

  // Handle description change
  const handleDescriptionChange = useCallback((value: string) =>
  {
    setDescription(value);
  }, []);

  // Handle body change (rich text editor)
  const handleBodyChange = useCallback((value: string) =>
  {
    setBody(value);
  }, []);

  // Handle attachment add
  const handleAttachmentAdd = useCallback((attachment: Attachment) =>
  {
    setAttachments(prev => [...prev, attachment]);
  }, []);

  // Handle attachment remove
  const handleAttachmentRemove = useCallback((attachmentId: number) =>
  {
    setAttachments(prev => prev.filter(a => a.AttachmentId !== attachmentId));
  }, []);

  // Reset form to initial state
  const resetForm = useCallback(
    (keepSelections: boolean = false) =>
    {
      setHours('');
      setHoursFormat(false);
      setHoursInitial('');
      setRemainingFieldInitial('');
      setActualFieldInitial('');
      setDate(todayStr());
      setDescription('');
      setBody('');
      setAttachments([]);
      setEstimatedField('');
      setRemainingField('');
      setActualField('');
      setRemainingId(undefined);
      setActualId(undefined);
      setBtnSubmit(false);
      setWarningReplyId(0);
      setWarningRemainingDays(false);
      setRemindRemaining(false);

      if (!keepSelections)
      {
        cascadeLoading.resetSelections();
      }

      validation.resetValidation(typesubmittime);
      customFields.setAddedConfigurations(null);
    },
    [typesubmittime, cascadeLoading, validation, customFields],
  );

  // Validate entire form
  const validateForm = useCallback((): boolean =>
  {
    let isValid = true;
    const { valForm, updateValidation } = validation;

    // Validate layer selection
    if (!cascadeLoading.selectedId)
    {
      updateValidation('selectedId', true);
      isValid = false;
    }
    else
    {
      updateValidation('selectedId', false);
    }

    // Validate date
    if (!date)
    {
      updateValidation('date', true);
      isValid = false;
    }
    else
    {
      updateValidation('date', false);
    }

    // Validate hours
    if (!hours || !validation.validateHoursNotZero(hours))
    {
      updateValidation('hours', true);
      isValid = false;
    }
    else
    {
      updateValidation('hours', false);
    }

    // Validate the hours range
    if (hours && !validation.validateHoursRange(hours, timeTracking.timeTrackingTeam))
    {
      updateValidation('hoursOutOfRange', true);
      isValid = false;
    }
    else
    {
      updateValidation('hoursOutOfRange', false);
    }

    // Validate body (description)
    if (!body && attachments.length === 0)
    {
      updateValidation('description', true);
      isValid = false;
    }
    else
    {
      updateValidation('description', false);
    }

    // Validate project, type, task if typesubmittime === 2
    if (typesubmittime === 2)
    {
      if (valForm.showProjects && !cascadeLoading.projectSelected)
      {
        updateValidation('validProject', true);
        isValid = false;
      }
      else
      {
        updateValidation('validProject', false);
      }

      if (valForm.showType && !cascadeLoading.typeSelected)
      {
        updateValidation('validType', true);
        isValid = false;
      }
      else
      {
        updateValidation('validType', false);
      }

      if (valForm.showTask && !cascadeLoading.taskSelected)
      {
        updateValidation('validTask', true);
        isValid = false;
      }
      else
      {
        updateValidation('validTask', false);
      }
    }

    // Validate phases if applicable
    if (valForm.showPhases && !isRealPhase(cascadeLoading.phaseSelected))
    {
      updateValidation('validPhases', true);
      isValid = false;
    }
    else
    {
      updateValidation('validPhases', false);
    }

    // Estimated is required (legacy): empty when the task/phase has no fields on the selected layer
    if (!estimatedField)
    {
      updateValidation('estimated', true);
      isValid = false;
    }
    else
    {
      updateValidation('estimated', false);
    }

    // Validate minutes per day
    if (date && hours)
    {
      const minutesToAdd = timeCalculations.timeToMinutes(hours);
      const isValidMinutes = validation.validateMinutesPerDateAdd(
        date,
        minutesToAdd,
        timeTracking.timeTracking,
        timeTracking.maxMinutesPerDay,
      );

      if (!isValidMinutes)
      {
        updateValidation('maxHours', true);
        isValid = false;
      }
      else
      {
        updateValidation('maxHours', false);
      }
    }

    return isValid;
  }, [
    validation,
    cascadeLoading,
    date,
    hours,
    body,
    attachments,
    estimatedField,
    typesubmittime,
    timeCalculations,
    timeTracking,
  ]);

  return {
    // Form state
    hours,
    setHours,
    date,
    setDate,
    description,
    setDescription,
    body,
    setBody,
    attachments,
    setAttachments,

    // Special fields
    estimatedField,
    setEstimatedField,
    remainingField,
    setRemainingField,
    actualField,
    setActualField,
    hoursInitial,
    setHoursInitial,
    remainingFieldInitial,
    setRemainingFieldInitial,
    actualFieldInitial,
    setActualFieldInitial,
    remainingId,
    setRemainingId,
    actualId,
    setActualId,

    // Hours format error
    hoursFormat,

    // Modal state
    openmodal,
    setOpenmodal,
    isEdit,
    btnSubmit,
    setBtnSubmit,

    // Delete dialog
    isOpenQuestionDelete,
    setIsOpenQuestionDelete,

    // Warnings
    warningReplyId,
    setWarningReplyId,
    warningRemainingDays,
    setWarningRemainingDays,
    remindRemaining,
    setRemindRemaining,

    // All hooks
    timeCalculations,
    validation,
    customFields,
    timeTracking,
    cascadeLoading,

    // Methods
    applyTimeFieldBaselines,
    handleHoursChange,
    handleHoursBlur,
    handleDateChange,
    handleEstimatedChange,
    handleRemainingChange,
    handleDescriptionChange,
    handleBodyChange,
    handleAttachmentAdd,
    handleAttachmentRemove,
    resetForm,
    validateForm,
  };
}
